/**
 * Patron-side · Campus Ambassador dashboard.
 *
 * Visible only to users with `is_campus_ambassador=true`. Lean by design:
 * a single page that shows the CA's link, funnel, and minimal invitee list
 * (full name + masked email + status only — no nudging, no PII leakage).
 *
 * Earnings / payouts are conditionally rendered behind the admin-controlled
 * `show_earnings_to_ca` toggle on the ambassador record. When OFF, the entire
 * earnings section is hidden — the CA sees only the funnel and invitee list.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDate } from "../lib/date";

const PKR = (n) => `₨ ${Number(n || 0).toLocaleString()}`;
const fmtDate = (iso) => formatDate(iso);

export default function AmbassadorDashboard() {
  const [me, setMe] = useState(null);
  const [invitees, setInvitees] = useState([]);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  // Role-aware — same dashboard shell is used by Campus Ambassadors and Growth
  // Leads (team volunteers). Copy adapts based on the viewing user's role.
  const isGrowthLead = user?.is_growth_lead || me?.role_kind === "growth_lead";

  useEffect(() => {
    (async () => {
      try {
        const [m, i] = await Promise.all([
          api.get("/me/ambassador"),
          api.get("/me/ambassador/invitees"),
        ]);
        setMe(m.data);
        setInvitees(i.data || []);
      } catch (e) {
        if (e.response?.status === 404) setErr("not-onboarded");
        else setErr("failed");
      }
    })();
  }, []);

  if (err === "not-onboarded") {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <h2 className="text-2xl font-bold tracking-tight text-neutral-900">
          {isGrowthLead ? "Growth dashboard not ready" : "Not yet a Campus Ambassador"}
        </h2>
        <p className="text-neutral-500 mt-2">
          {isGrowthLead
            ? "Your Growth Lead record is still being set up. Refresh in a moment — if this persists, ping your admin contact."
            : <>If you&apos;ve been onboarded as a Campus Ambassador, your admin contact will let you know. This page only lights up after that happens.</>}
        </p>
        <Button onClick={() => nav("/panelist")} className="rounded-full mt-4">Back to dashboard</Button>
      </div>
    );
  }
  if (err === "failed") return <div className="p-8 text-red-600">Failed to load. Please retry.</div>;
  if (!me) return <div className="p-8 text-neutral-400">Loading…</div>;

  // Growth Lead uses the peer-referral URL scheme (/r/{referral_code}) — per
  // your decision to reuse the existing patron referral routing. Campus
  // Ambassadors keep /cap/{slug}.
  const link = isGrowthLead
    ? `${window.location.origin}/r/${(user?.referral_code || me.slug || "").toUpperCase()}`
    : `${window.location.origin}/cap/${me.slug}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="ambassador-dashboard">
      {/* ── Header card with link + funnel ─── */}
      <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-6">
        <div className="text-xs font-bold uppercase tracking-wider text-mango" data-testid="ambassador-role-pill">
          {isGrowthLead ? "GROWTH LEAD · TEAM" : "CAMPUS AMBASSADOR"}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-neutral-900 mt-1">
          {isGrowthLead ? "Your growth link" : "Your ambassador link"}
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          {isGrowthLead
            ? <>Share this link across your networks. Every signup is attributed to you — you&apos;ll see the funnel below in real time.</>
            : <>Share this link with your campus. Each person who signs up, completes their profile, and takes their first survey counts as a <strong>confirmed</strong> referral.</>}
        </p>

        <div className="mt-4 flex items-stretch rounded-full border border-neutral-200 overflow-hidden">
          <div className="flex-1 px-4 py-3 bg-neutral-50 font-mono text-sm overflow-x-auto whitespace-nowrap" data-testid="ambassador-link-text">{link}</div>
          <button onClick={copyLink} className="bg-mango text-white px-5 font-bold uppercase tracking-wider text-xs hover:bg-mango/90 transition-colors" data-testid="ambassador-copy-link">
            {copied ? "Copied ✓" : "Copy"}
          </button>
        </div>

        {/* ── Funnel ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
          {/* "Invited" tile removed (Phase 8.62) — it always equalled
              n_signed_up because we don't track raw link clicks. The
              3-tile funnel below is monotone and honest. */}
          <FunnelStat label="Signed up" value={me.n_signed_up} hint="Created an oodel account" />
          <FunnelStat label="Profile complete" value={me.n_profile_complete} hint={`Filled out their full profile — ${(me.n_signed_up || 0) - (me.n_profile_complete || 0)} still pending`} />
          <FunnelStat label="Confirmed" value={me.n_confirmed} accent="mango" hint={`Profile + first survey — the goal. ${(me.n_profile_complete || 0) - (me.n_confirmed || 0)} did profile but haven't taken first survey yet`} />
        </div>

        {me.status !== "active" && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 font-medium">
            Your ambassador status is currently <strong>{me.status?.toUpperCase()}</strong>. New signups via your link are temporarily redirected to your peer referral link.
          </div>
        )}
      </div>

      {/* ── Optional earnings card (admin-toggleable) ─── */}
      {me.show_earnings && (
        <div className="bg-neutral-900 text-white rounded-2xl shadow-sm p-6" data-testid="ambassador-earnings">
          <div className="text-xs font-bold uppercase tracking-wider text-mango">EARNINGS</div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <Money label="Per conversion" value={PKR(me.rate_pkr)} sub="Your rate" />
            <Money label="Owed (pending)" value={PKR(me.pending_pkr)} sub="Will be paid out next batch" accent />
            <Money label="Paid lifetime" value={PKR(me.paid_pkr)} sub="Already disbursed" />
          </div>
          {(me.payout_batches || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/10">
              <div className="text-xs font-bold uppercase tracking-wider text-mango mb-2">RECENT PAYOUT BATCHES</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-neutral-400">
                    <th className="text-left py-1">Period</th>
                    <th className="text-left py-1">Conversions</th>
                    <th className="text-left py-1">Total</th>
                    <th className="text-left py-1">Status</th>
                    <th className="text-left py-1">Paid on</th>
                  </tr>
                </thead>
                <tbody>
                  {(me.payout_batches || []).map((b) => (
                    <tr key={b.id} className="border-t border-white/5">
                      <td className="py-1.5">{b.period_label}</td>
                      <td className="py-1.5">{b.n_conversions}</td>
                      <td className="py-1.5 font-bold text-mango">{PKR(b.total_pkr)}</td>
                      <td className="py-1.5 uppercase font-bold">{b.status}</td>
                      <td className="py-1.5 text-neutral-400">{fmtDate(b.paid_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Invitees table — minimal, privacy-preserving ─── */}
      <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-neutral-100">
          <h2 className="font-bold text-sm text-neutral-900">YOUR INVITEES ({invitees.length})</h2>
        </div>
        {invitees.length === 0 ? (
          <div className="p-8 text-center text-neutral-400 text-sm">
            No one has signed up through your link yet. Start sharing!
          </div>
        ) : (
          <>
            {/* Mobile — stacked cards. The 4-column table below overflows a
                narrow viewport and silently clips the Joined/Status columns
                (parent has overflow-hidden for the rounded corners), so
                phones need their own layout rather than a squeezed table. */}
            <div className="sm:hidden divide-y divide-neutral-100" data-testid="ambassador-invitees-mobile">
              {invitees.map((i, idx) => (
                <div key={idx} className="px-4 py-3" data-testid={`ambassador-invitee-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-neutral-900 text-sm truncate">{i.name}</span>
                    <InviteeStatus s={i.status} />
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-neutral-500 font-mono text-xs truncate">{i.email_masked}</span>
                    <span className="text-neutral-400 text-xs shrink-0">{fmtDate(i.joined_at)}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop / tablet — full table. */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-400">Name</th>
                    <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-400">Email</th>
                    <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-400">Joined</th>
                    <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invitees.map((i, idx) => (
                    <tr key={idx} className="border-b border-neutral-100" data-testid={`ambassador-invitee-desktop-${idx}`}>
                      <td className="px-4 py-3 font-bold text-neutral-900">{i.name}</td>
                      <td className="px-4 py-3 text-neutral-500 font-mono text-xs">{i.email_masked}</td>
                      <td className="px-4 py-3 text-neutral-400 text-xs">{fmtDate(i.joined_at)}</td>
                      <td className="px-4 py-3"><InviteeStatus s={i.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FunnelStat({ label, value, hint, accent }) {
  return (
    <div className={`rounded-xl p-4 border ${accent === "mango" ? "border-mango/30 bg-mango/5" : "border-neutral-200 bg-white"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">{label}</div>
      <div className="text-3xl font-bold tabular-nums tracking-tight mt-1 text-neutral-900">{value ?? 0}</div>
      {hint && <div className="text-[10px] text-neutral-400 mt-1">{hint}</div>}
    </div>
  );
}
function Money({ label, value, sub, accent }) {
  return (
    <div className={`rounded-xl p-3 ${accent ? "bg-mango/10" : "bg-white/5"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-mango">{label}</div>
      <div className="text-2xl font-bold tabular-nums tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] text-neutral-400 mt-1">{sub}</div>}
    </div>
  );
}
function InviteeStatus({ s }) {
  const map = {
    "Signed up": "text-neutral-500 bg-neutral-100",
    "Profile complete": "text-blue-700 bg-blue-50",
    "Confirmed": "text-emerald-700 bg-emerald-50 font-bold",
  };
  return <span className={`text-[10px] tracking-widest font-bold px-2 py-1 rounded-full ${map[s] || "text-neutral-500 bg-neutral-100"}`}>{(s || "—").toUpperCase()}</span>;
}
